'use client';

import { Box, Button, Flex, FormLabel, Input, Typography } from '@coolblue-development/freddy';
import { CAMPAIGN_TYPE } from '@features/product/components/OrderBlock/FreeWashingOrDryingProposition/constants';
import { useTranslation } from '@i18n/useTranslation';
import { useActionState, useCallback, useEffect, useId, useState, useTransition } from 'react';

import { subscribeToNewsletter } from './actions';
import type { FormStateProps } from './types';

const initialState: FormStateProps = {
  success: false,
  error: false
};

type SubscriptionFormProps = {
  onSuccessAction?: () => void;
  type: CAMPAIGN_TYPE;
};

export default function SubscriptionForm({ onSuccessAction, type }: SubscriptionFormProps) {
  const { t } = useTranslation('webshop.productPage.freeWashing');

  const [state, formAction, formPending] = useActionState(subscribeToNewsletter, initialState);
  const [transitionPending, startTransition] = useTransition();

  const baseId = useId();
  const ids = {
    helper: `${baseId}-helper`,
    error: `${baseId}-error`,
    email: `${baseId}-email`
  };

  const [email, setEmail] = useState<string>('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const validate = useCallback(() => {
    if (email.length < 1) {
      setValidationError(t('emailEmpty'));

      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(email)) {
      setValidationError(t('emailInvalid'));

      return false;
    }

    setValidationError(null);
    return true;
  }, [email, t]);

  const submit = useCallback(() => {
    if (!validate()) return;

    const formData = new FormData();
    formData.append('email', email);
    formData.append('type', type);

    startTransition(() => {
      formAction(formData);
    });
  }, [email, formAction, type, validate]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
  };

  useEffect(() => {
    if (state?.success && onSuccessAction) {
      onSuccessAction();
    }
  }, [state?.success, onSuccessAction]);

  const hasError = !!validationError || !!state?.error;
  const isSuccess = !!state?.success;

  const ariaDescribedBy = hasError ? undefined : ids.helper;

  return (
    <Box>
      <FormLabel htmlFor={ids.email}>{t('emailFieldLabel')}</FormLabel>

      <Flex gap={200} alignItems='flex-end' mt={100}>
        <Box flex={1}>
          <Input
            id={ids.email}
            type='email'
            value={email}
            onChange={e => setEmail(e.target.value)}
            onBlur={validate}
            validation={validationError ? 'error' : undefined}
            aria-describedby={ariaDescribedBy}
            aria-invalid={hasError || undefined}
            aria-required='true'
            autoComplete='email'
            inputMode='email'
            onKeyDown={handleKeyDown}
          />
        </Box>

        <Button
          type='button'
          variant='primary'
          size='small'
          loading={formPending || transitionPending}
          disabled={formPending || transitionPending || isSuccess}
          onClick={submit}
        >
          {isSuccess ? t('subscribeButton') + ' ✓' : t('subscribeButton')}
        </Button>
      </Flex>

      {!hasError && (
        <Box id={ids.helper}>
          <Typography mt={100} color='subtle'>
            {t('emailFieldHelperText')}
          </Typography>
        </Box>
      )}

      {hasError && (
        <Box id={ids.error} role='alert'>
          {state?.error && (
            <Typography mt={100} color='sentimentDanger'>
              {t('subscriptionFailedMessage')}
            </Typography>
          )}
          {validationError && (
            <Typography mt={100} color='sentimentDanger'>
              {validationError}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
